/**
 * Centralized user-facing strings for the delivery add-ons UI.
 *
 */

import { NoticeAddOnPricingConfig } from 'lib/pricing/noticeAddOns/types';
import {
  formatCents,
  getWetSigFeeCents,
  getGroundShippingFeeCents,
  PaperType
} from './pricingConfig';

export const ADDONS_COPY = {
  affidavitStepTitle: 'Affidavit options',
  affidavitStepCaption:
    'Choose how you want this affidavit notarized and delivered. A digital copy is always included.',

  // Wet-sig-required: first affidavit included, extras are paid.
  wetSigAdditionalLegend:
    'Would you like to receive more than one wet-signature affidavit?',
  wetSigAdditionalNoOption: 'I only need one affidavit',

  affidavitTypeLegend: 'How would you like your affidavit notarized?',
  ronOptionLabel: 'Digital Affidavit (RON)',
  ronOptionDescription:
    'Remotely notarized and emailed. Available for download after the final publication.',
  wetSigOptionDescription:
    'Physically signed in wet ink by a notary and mailed to the address you provide.',

  // Sub-question after choosing digital (RON).
  deliveryLegend: 'How would you like to receive this affidavit?',
  // Top-level question when there is no notarization choice (non-AA / RON-only).
  nonAaDeliveryLegend: 'How would you like to receive your affidavit?',
  deliveryEmailOption: 'Email',
  deliveryEmailDescription:
    'Emailed and available for download after the final publication.',
  deliveryMailOptionDescription:
    'A printed copy is mailed via standard USPS to the address you provide.',

  // Notice-type preset addresses: publisher-paid, so the customer is told, not asked.
  noticeTypeMailingIntro:
    'The newspaper has configured this notice type to send a copy of the affidavit to a specified address.',
  noticeTypeMailingNoCharge:
    'The newspaper covers this copy, so there is no charge to you. You can still choose how you would like to receive your own affidavit below.',
  noticeTypeMailingAlsoToYourAddress:
    'A copy of the affidavit will be mailed to the specified address and an address of your choosing. You are only charged for your own copy.',
  noticeTypeMailingWetSig:
    'A copy of the affidavit will be mailed to the specified address and the original will be mailed to the address you choose.',
  noticeTypeMailingMissingAddress: 'No address configured',
  noticeTypeMailingInvalid:
    'This address is incomplete, so the required affidavit copy cannot be mailed. Please contact the newspaper to have it corrected before placing this notice.',

  shippingLegend: 'Shipping speed',
  shippingHelpText:
    'Shipping speed applies once the affidavit is completed. If selecting multiple addresses, the selected shipping speed applies to the whole order.',

  invoiceToggleLegend: 'How would you like to receive this invoice?',
  invoiceEmailOption: 'Email the invoice',
  invoiceMailWarning:
    'If you choose to mail the invoice, please note that the invoice may arrive after the payment deadline for notices that require upfront payment. Please ensure the invoice is paid on time.',

  pricingSummaryTitle: 'Payment summary',
  pricingSummaryEmpty: 'No paid add-ons selected.',
  pricingSummaryTotal: 'Estimated add-on total',

  // Per-paper-type SLA messaging shown above the affidavit options.
  slaNonAA:
    'This newspaper is configured to provide a digital affidavit. It will be uploaded to your account after the last publication date.',
  slaWetSigRequired:
    'This newspaper is configured to provide wet-signature affidavits. Your affidavit is expected to be signed and notarized within four business days of the last publication date. If you wish to receive more than one affidavit, you may request additional affidavits to be mailed to your preferred address for an additional cost.',
  slaWetSigRequiredNoMailing:
    'This newspaper is configured to provide wet-signature affidavits. Your affidavit is expected to be signed and notarized within four business days of the last publication date.',
  slaWetSigNotRequired:
    'This newspaper is configured to provide affidavits completed by Remote Online Notarization. Affidavits are expected to be completed within two business days of the last publication date. If you prefer to receive a wet signature affidavit, affidavits will be signed, notarized, and mailed within four days of the last publication date.',
  // RON SLA without the wet-signature sentence, for papers that don't offer it.
  slaWetSigNotOffered:
    'This newspaper is configured to provide affidavits completed by Remote Online Notarization. Affidavits are expected to be completed within two business days of the last publication date.'
};

/**
 * The standard-SLA message shown above the affidavit options for a given paper
 * type. Returns undefined for paper types without dedicated copy. `wetSigOffered`
 * picks the RON SLA variant (with vs without the wet-signature sentence).
 */
export function affidavitSlaMessage(
  paperType: PaperType,
  wetSigOffered = true
): string | undefined {
  switch (paperType) {
    case PaperType.NON_AA:
      return ADDONS_COPY.slaNonAA;
    case PaperType.WET_SIG_REQUIRED:
      return ADDONS_COPY.slaWetSigRequired;
    case PaperType.WET_SIG_REQUIRED_NO_MAILING:
      return ADDONS_COPY.slaWetSigRequiredNoMailing;
    case PaperType.WET_SIG_NOT_REQUIRED:
      return wetSigOffered
        ? ADDONS_COPY.slaWetSigNotRequired
        : ADDONS_COPY.slaWetSigNotOffered;
    default:
      return undefined;
  }
}

/**
 * What the customer is told about the notice type's preset copies, given what
 * they have selected for their own affidavit.
 */
export function noticeTypeMailingOutcome({
  invalid,
  isWetSig,
  mailsOwnCopy
}: {
  invalid: boolean;
  isWetSig: boolean;
  mailsOwnCopy: boolean;
}): string {
  if (invalid) {
    return ADDONS_COPY.noticeTypeMailingInvalid;
  }
  if (isWetSig) {
    return ADDONS_COPY.noticeTypeMailingWetSig;
  }
  if (mailsOwnCopy) {
    return ADDONS_COPY.noticeTypeMailingAlsoToYourAddress;
  }
  return ADDONS_COPY.noticeTypeMailingNoCharge;
}

/**
 * Wet-signature option label. When the publisher covers the first wet sig, the
 * first copy is free and only additional copies are charged.
 */
export function wetSigOptionLabel(
  config: NoticeAddOnPricingConfig,
  firstWetSigFree = false
): string {
  if (firstWetSigFree) {
    return `Wet-signature affidavit — first copy free, then ${formatCents(
      getWetSigFeeCents(config)
    )} per additional affidavit`;
  }
  return `Wet-signature affidavit — ${formatCents(
    getWetSigFeeCents(config)
  )} per affidavit`;
}

/** "I would like more than one" opt-in option, with the per-affidavit price. */
export function wetSigAdditionalYesOptionLabel(
  config: NoticeAddOnPricingConfig
): string {
  return `I would like more than one — ${formatCents(
    getWetSigFeeCents(config)
  )} per affidavit`;
}

export function invoiceMailOptionLabel(
  config: NoticeAddOnPricingConfig
): string {
  return `Mail the invoice — ${formatCents(
    getGroundShippingFeeCents(config)
  )} per copy`;
}
